import React from 'react';
import moment from 'moment';
import ModalOmni from './ModalOmni';
import { Button } from '@mui/material';
import ButtonOmni from './ButtonOmni';

// snake_case / lower text -> "Title Case"
const formatLabel = (key) =>
	String(key || '')
		.split('_')
		.filter(Boolean)
		.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
		.join(' ');

// Audio links inside an interaction's event_data are rendered as inline players.
const AUDIO_EXTENSIONS = ['.mp3', '.mpeg', '.mpga', '.mp2', '.wav', '.ogg', '.oga', '.m4a', '.aac', '.webm'];

const isAudioUrl = (value) => {
	if (typeof value !== 'string') return false;
	const trimmed = value.trim();
	if (!/^https?:\/\//i.test(trimmed)) return false;
	const path = trimmed.split(/[?#]/)[0].toLowerCase();
	return AUDIO_EXTENSIONS.some(ext => path.endsWith(ext));
};

const fileNameFromUrl = (url) => {
	try {
		const path = String(url).split(/[?#]/)[0];
		return decodeURIComponent(path.substring(path.lastIndexOf('/') + 1)) || 'Audio';
	} catch {
		return 'Audio';
	}
};

const MediaModal = ({ mediaModal, mediaTypeForModal, mediaForModal, title, setMediaModal, setIsLoadingDoc, isLoadingDoc }) => {
	// Only one interaction audio should play at a time.
	const handleAudioPlay = (e) => {
		const current = e.currentTarget;
		document.querySelectorAll('audio[data-call-journey-audio="true"]').forEach(el => {
			if (el !== current && !el.paused) el.pause();
		});
	};

	const handleDocumentLoad = () => {
		setIsLoadingDoc(false);
	};

	const handleDocumentError = () => {
		setIsLoadingDoc(false);
		console.error('Failed to load document in iframe');
	};

	const openInNewTab = () => {
		if (mediaTypeForModal === 'INTERACTIVE') {
			return;
		}
		window.open(mediaForModal, '_blank', 'noopener,noreferrer');
	};



	const modalContent = (
		<div style={{ backgroundColor: '#f6f7f9', padding: '2em 1em' }}>
			{(() => {
				switch (mediaTypeForModal) {
					case 'TEXT':
						return (
							<div style={{ maxHeight: '70vh', overflow: 'auto', padding: '20px' }}>
								{mediaForModal && (
									<div
										style={{
											backgroundColor: '#fff',
											padding: '16px',
											borderRadius: '8px',
											marginBottom: '20px',
											border: '1px solid #e0e0e0',
											lineHeight: '1.6',
											fontSize: '14px'
										}}
									>
										<p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{mediaForModal}</p>
									</div>
								)}
							</div>
						);
					case 'IMAGE':
						return (
							<div style={{ textAlign: 'center' }}>
								<img
									style={{ maxHeight: '70vh', maxWidth: '100%', objectFit: 'contain' }}
									src={mediaForModal}
									alt="Shared content"
									onError={e => {
										e.target.style.display = 'none';
										e.target.nextSibling.style.display = 'block';
									}}
								/>
								<div
									style={{
										display: 'none',
										padding: '40px',
										color: 'rgba(0, 0, 0, 0.6)'
									}}
								>
									<p>Failed to load image</p>
									<Button variant="outlined" onClick={openInNewTab} style={{ marginTop: '10px' }}>
										Open Original Link
									</Button>
								</div>
							</div>
						);

					case 'VIDEO':
						return (
							<div style={{ textAlign: 'center' }}>
								<video
									controls
									style={{ maxHeight: '70vh', maxWidth: '100%' }}
									src={mediaForModal}
									onError={e => {
										console.error('Video failed to load:', e);
									}}
								>
									Your browser does not support the video tag.
								</video>
							</div>
						);

					case 'DOCUMENT':
						return (
							<div style={{ height: '70vh', width: '100%' }}>
								<div style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
									<ButtonOmni
										label="Open in New Tab"
										variant="outlined"
										size="small"
										onClick={openInNewTab}
									/>
									<ButtonOmni
										variant="outlined"
										size="small"
										onClick={() =>
											window.open(
												`https://docs.google.com/gview?url=${encodeURIComponent(
													mediaForModal
												)}&embedded=true`,
												'_blank'
											)
										}
										label="Open in Google Docs Viewer"
									/>
								</div>

								{isLoadingDoc && (
									<div
										style={{
											position: 'absolute',
											top: '50%',
											left: '50%',
											transform: 'translate(-50%, -50%)',
											backgroundColor: 'rgba(255, 255, 255, 0.9)',
											padding: '20px',
											borderRadius: '8px',
											zIndex: 10
										}}
									>
										<p>Loading document, please wait...</p>
									</div>
								)}

								<div style={{ position: 'relative', height: 'calc(100% - 50px)' }}>
									<iframe
										className="doc"
										onLoad={handleDocumentLoad}
										onError={handleDocumentError}
										style={{
											width: '100%',
											height: '100%',
											border: '1px solid #ddd',
											borderRadius: '4px'
										}}
										src={`https://docs.google.com/gview?url=${encodeURIComponent(
											mediaForModal
										)}&embedded=true`}
										title="Document Viewer"
									/>
								</div>
							</div>
						);

					case 'INTERACTIVE':
						const interactiveData = mediaForModal.interactive;
						return (
							<div style={{ maxHeight: '70vh', overflow: 'auto', padding: '20px' }}>
								{/* Header Image */}
								{interactiveData?.header_type === 'image' && interactiveData?.header_value && (
									<div style={{ marginBottom: '20px', textAlign: 'center' }}>
										<img
											src={interactiveData.header_value}
											alt="Interactive message header"
											style={{
												maxWidth: '100%',
												maxHeight: '300px',
												objectFit: 'contain',
												border: '1px solid #ddd',
												borderRadius: '8px'
											}}
											onError={e => {
												e.target.style.display = 'none';
											}}
										/>
									</div>
								)}

								{/* Body Text */}
								{interactiveData?.body && (
									<div
										style={{
											backgroundColor: '#fff',
											padding: '16px',
											borderRadius: '8px',
											marginBottom: '20px',
											border: '1px solid #e0e0e0',
											lineHeight: '1.6',
											fontSize: '14px'
										}}
									>
										<p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{interactiveData.body}</p>
									</div>
								)}

								{/* Footer */}
								{interactiveData?.footer && (
									<div
										style={{
											fontSize: '12px',
											color: 'rgba(0, 0, 0, 0.6)',
											marginBottom: '20px',
											fontStyle: 'italic'
										}}
									>
										{interactiveData.footer}
									</div>
								)}

								{/* Interactive Buttons */}
								{interactiveData?.actions_list?.buttons && (
									<div
										style={{
											display: 'flex',
											flexDirection: 'column',
											gap: '10px',
											alignItems: 'center'
										}}
									>
										<p
											style={{
												fontSize: '12px',
												color: 'rgba(0, 0, 0, 0.6)',
												margin: '0 0 10px 0'
											}}
										>
											Interactive Buttons:
										</p>
										{interactiveData.actions_list.buttons.map((button, index) => (
											<ButtonOmni
												key={index}
												variant="contained"
												color="primary"
												style={{
													minWidth: '200px',
													textTransform: 'none'
												}}
												label={button.reply?.title || button.title || `Button ${index + 1}`}
											/>
										))}
									</div>
								)}

								{/* Message Info */}
								<div
									style={{
										marginTop: '20px',
										padding: '12px',
										backgroundColor: 'rgba(0, 0, 0, 0.05)',
										borderRadius: '4px',
										fontSize: '12px',
										color: 'rgba(0, 0, 0, 0.6)'
									}}
								>
									<p style={{ margin: '0 0 4px 0' }}>
										<strong>Type:</strong> {interactiveData?.type || 'Unknown'}
									</p>
									<p style={{ margin: 0 }}>
										<strong>Header Type:</strong> {interactiveData?.header_type || 'None'}
									</p>
								</div>
							</div>
						);

					case 'INTERACTIONS': {
						const interactions = Array.isArray(mediaForModal) ? mediaForModal : [];
						if (interactions.length === 0) {
							return (
								<div
									style={{
										textAlign: 'center',
										padding: '40px',
										color: 'rgba(0, 0, 0, 0.6)'
									}}
								>
									<p>No interactions available</p>
								</div>
							);
						}
						return (
							<div style={{ maxHeight: '70vh', overflow: 'auto', padding: '20px' }}>
								{interactions.map((item, index) => (
									<div
										key={item.id || index}
										style={{
											backgroundColor: '#fff',
											padding: '16px',
											borderRadius: '8px',
											marginBottom: '16px',
											border: '1px solid #e0e0e0'
										}}
									>
										<div
											style={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
												gap: '12px',
												marginBottom: item.node_path || (item.event_data && Object.keys(item.event_data).length) ? '10px' : 0
											}}
										>
											<span style={{ fontWeight: 600, fontSize: '14px' }}>
												{formatLabel(item.event_type) || 'Event'}
											</span>
											{item.dt && (
												<span style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.6)', whiteSpace: 'nowrap' }}>
													{moment(item.dt).format('MMM Do YYYY, h:mm:ss A')}
												</span>
											)}
										</div>

										{item.node_path && (
											<div style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.6)', marginBottom: '8px' }}>
												<strong>Node Path:</strong> {item.node_path}
											</div>
										)}

										{item.event_data && Object.keys(item.event_data).length > 0 && (
											<div
												style={{
													display: 'grid',
													gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
													gap: '4px 16px',
													fontSize: '13px',
													color: 'rgba(0, 0, 0, 0.8)'
												}}
											>
												{Object.entries(item.event_data).map(([key, value]) => (
													isAudioUrl(value) ? (
														<div key={key} style={{ gridColumn: '1 / -1' }}>
															<strong>{formatLabel(key)}:</strong>
															<div
																style={{
																	marginTop: '6px',
																	padding: '10px 12px',
																	backgroundColor: 'rgba(25, 118, 210, 0.06)',
																	border: '1px solid rgba(25, 118, 210, 0.2)',
																	borderRadius: '10px'
																}}
															>
																<div
																	style={{
																		fontSize: '12px',
																		fontWeight: 600,
																		color: 'rgba(0, 0, 0, 0.75)',
																		whiteSpace: 'nowrap',
																		overflow: 'hidden',
																		textOverflow: 'ellipsis',
																		marginBottom: '6px'
																	}}
																	title={fileNameFromUrl(value)}
																>
																	{fileNameFromUrl(value)}
																</div>
																<audio
																	controls
																	src={value}
																	data-call-journey-audio="true"
																	onPlay={handleAudioPlay}
																	style={{ width: '100%', height: '36px' }}
																>
																	Your browser does not support the audio element.
																</audio>
															</div>
														</div>
													) : (
														<div key={key}>
															<strong>{formatLabel(key)}:</strong>{' '}
															{value === null || value === undefined || value === ''
																? '-'
																: typeof value === 'object'
																	? JSON.stringify(value)
																	: String(value)}
														</div>
													)
												))}
											</div>
										)}
									</div>
								))}
							</div>
						);
					}

					default:
						return (
							<div
								style={{
									textAlign: 'center',
									padding: '40px',
									color: 'rgba(0, 0, 0, 0.6)'
								}}
							>
								<p>Unsupported media type: {mediaTypeForModal}</p>
							</div>
						);
				}
			})()}
		</div>
	);

	return (
		<ModalOmni
			open={mediaModal}
			handleClose={() => setMediaModal(false)}
			title={title || `${mediaTypeForModal}`}
			content={modalContent}
			maxWidth="lg"
		/>
	);
};

export default MediaModal;
